import { useEffect, useState } from 'react';
import axios from 'axios';
import { DollarSign, Wallet, TrendingUp, TrendingDown, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CointrackerOverview() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState({
    totalBalance: 0,
    totalBalanceChange: 0,
    walletsCount: 0,
    transactionsCount: 0,
    pendingJournalEntries: 0,
  });
  const [walletBalances, setWalletBalances] = useState([]);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API_URL}/cointracker/overview`, { headers });
      setStats(response.data.stats);
      setWalletBalances(response.data.wallets || []);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
      // Set default values on error
      setStats({
        totalBalance: 0,
        totalBalanceChange: 0,
        walletsCount: 0,
        transactionsCount: 0,
        pendingJournalEntries: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/cointracker/wallets/sync-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All wallets synced successfully');
      fetchOverview();
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync wallets');
    } finally {
      setSyncing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex justify-end">
        <Button
          onClick={handleSyncAll}
          disabled={syncing}
          className="bg-white text-black hover:bg-zinc-200"
        >
          {syncing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync All Wallets
            </>
          )}
        </Button>
      </div>

      {/* Total Balance Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-400 uppercase tracking-wider font-medium">Total Portfolio Value</p>
              <p className="text-5xl md:text-6xl font-bold text-white mt-2 tracking-tight">
                {formatCurrency(stats.totalBalance)}
              </p>
              {stats.totalBalanceChange !== 0 && (
                <div className={`flex items-center gap-1 mt-2 ${stats.totalBalanceChange >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {stats.totalBalanceChange >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">
                    {stats.totalBalanceChange >= 0 ? '+' : ''}{stats.totalBalanceChange.toFixed(2)}% (24h)
                  </span>
                </div>
              )}
            </div>
            <div className="bg-green-500/10 p-4 rounded-lg">
              <DollarSign className="w-10 h-10 text-green-400" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Wallets</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.walletsCount}</p>
              </div>
              <div className="bg-blue-500/10 p-3 rounded-lg">
                <Wallet className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Transactions</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.transactionsCount}</p>
              </div>
              <div className="bg-purple-500/10 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Pending Entries</p>
                <p className="text-3xl font-bold text-white mt-1">{stats.pendingJournalEntries}</p>
              </div>
              <div className="bg-yellow-500/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Wallets Breakdown */}
      {walletBalances.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Wallet Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {walletBalances.map((wallet) => (
                <div
                  key={wallet.wallet_id}
                  className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      wallet.chain === 'solana' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                    }`}>
                      <Wallet className={`w-5 h-5 ${
                        wallet.chain === 'solana' ? 'text-purple-400' : 'text-blue-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{wallet.name}</p>
                      <p className="text-xs text-zinc-500 font-mono">
                        {wallet.address?.slice(0, 6)}...{wallet.address?.slice(-4)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-semibold">{formatCurrency(wallet.balance_usd)}</p>
                    <p className="text-xs text-zinc-500">{wallet.chain}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {walletBalances.length === 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12">
            <div className="text-center">
              <Wallet className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">No wallets added yet</h3>
              <p className="text-zinc-400 text-sm mb-4">
                Add your first wallet to start tracking your crypto portfolio
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
